import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { setActiveMatterUser } from '../lib/activeMatter';
import { getAccessToken, setManagedToken } from '../lib/authSession';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(getAccessToken());
  const [authMethod, setAuthMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutError, setLogoutError] = useState(null);
  const epoch = useRef(0);
  const callbackAtMount = useRef(new URLSearchParams(window.location.hash.slice(1)).has('session_id'));
  setActiveMatterUser(user?.id);
  const api = useMemo(() => {
    const instance = axios.create({ baseURL: `${API_URL}/api`, withCredentials: true });
    instance.interceptors.request.use(config => {
      const t = getAccessToken();
      config.headers['X-App-Lang'] = localStorage.getItem('notary_lang') || 'es';
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    return instance;
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem('notary_token');
    localStorage.removeItem('notary_auth_method');
    setManagedToken(null); setToken(null); setUser(null); setAuthMethod(null);
    setLogoutError(null);
  }, []);

  const fetchUser = useCallback(async () => {
    const generation = epoch.current;
    try {
      const { data } = await api.get('/auth/session');
      if (generation !== epoch.current) return;
      if (data.auth_method === 'google') {
        localStorage.removeItem('notary_token');
        localStorage.setItem('notary_auth_method', 'google');
        setManagedToken(data.access_token); setToken(data.access_token);
      }
      setAuthMethod(data.auth_method); setUser(data.user);
      return data.user;
    } catch (e) {
      if (generation === epoch.current && [401, 403].includes(e.response?.status)) clear();
      return null;
    } finally { if (generation === epoch.current) setLoading(false); }
  }, [api, clear]);

  useEffect(() => {
    if (callbackAtMount.current) { setLoading(false); return; }
    fetchUser();
  }, [fetchUser]);
  useEffect(() => {
    if (authMethod !== 'google') return;
    const refresh = () => { if (!window.location.hash.includes('session_id=')) fetchUser(); };
    const timer = setInterval(refresh, 50 * 60 * 1000);
    window.addEventListener('focus', refresh);
    return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [authMethod, fetchUser]);

  const completeLogin = async (response) => {
    setLogoutError(null);
    epoch.current += 1;
    const { access_token } = response.data;
    setManagedToken(null);
    localStorage.removeItem('notary_auth_method');
    localStorage.setItem('notary_token', access_token);
    setToken(access_token); setAuthMethod('password');
    await fetchUser();
    return response.data;
  };
  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data.mfa_required ? response.data : completeLogin(response);
  };
  const verifyMfa = async (mfa_token, code) => completeLogin(await api.post('/auth/2fa/verify', { mfa_token, code }));
  const register = async (email, username, password, consentData = null) => completeLogin(await api.post('/auth/register', { email, username, password, ...(consentData ? { consent_data: consentData } : {}) }));

  const completeManaged = async (data) => {
    setLogoutError(null);
    if (!data.access_token || data.auth_method !== 'google') throw new Error('No se pudo completar la sesión de Google');
    epoch.current += 1;
    localStorage.removeItem('notary_token');
    localStorage.setItem('notary_auth_method', 'google');
    setManagedToken(data.access_token); setToken(data.access_token); setAuthMethod('google');
    const me = await fetchUser();
    if (!me) throw new Error('No se pudo confirmar la sesión. Intenta ingresar nuevamente.');
    return me;
  };

  const logout = async () => {
    setLogoutError(null);
    try { await api.post('/auth/logout'); }
    catch (e) { if (authMethod === 'google') { setLogoutError('No se pudo cerrar la sesión en el servidor. Tu sesión sigue abierta; puedes reintentar.'); return false; } }
    epoch.current += 1; clear(); return true;
  };

  return <AuthContext.Provider value={{ user, token, authMethod, loading, logoutError, login, verifyMfa, register, logout, api, setToken, setUser, refreshUser: fetchUser, completeManaged }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};